import { Button, UndoSVG } from "@newsela/angelou";
import { actionNames, objectTypes, withTrackEvent } from "@newsela/snitch";
import { useEffect, useState } from "react";

import AIButton from "@/components/AIComponents/AiButton";
import { useExportablesContext } from "@/components/AIComponents/ExportablesModal/ExportablesContextProvider/useExportablesContext";
import { getOptionalFieldsList } from "@/components/AIComponents/ExportablesModal/utils";

import type { SourceAnalysisOrganizerFormData } from "@/types/models/Exportables.interface";

const ButtonWithTrackEvent = withTrackEvent(Button);
const AIButtonWithTrackEvent = withTrackEvent(AIButton);

const LunaAutoFillButton = () => {
  const {
    exportablesMetadata,
    formData,
    setFormData,
    selectedExportable,
    content,
  } = useExportablesContext();

  const [isAutoFilled, setIsAutoFilled] = useState(false);

  const setiIdentifier = selectedExportable?.seti_identifier;

  const optionalFieldList = getOptionalFieldsList(setiIdentifier);

  // Reset toggle when switching templates
  useEffect(() => {
    setIsAutoFilled(false);
  }, [setiIdentifier]);

  const sourceAnalysisSetiData = exportablesMetadata?.primary_source_analysis;

  if (!sourceAnalysisSetiData) return null;

  const handleAutoFill = () => {
    const currentFormData = (formData[setiIdentifier] ||
      {}) as SourceAnalysisOrganizerFormData;

    setFormData((prev) => ({
      ...prev,
      [setiIdentifier]: {
        ...sourceAnalysisSetiData,
        articleShortTitle: currentFormData.articleShortTitle,
        directions: currentFormData.directions,
      },
    }));

    setIsAutoFilled(true);
  };

  const handleRestoreToDefault = () => {
    const currentFormData = (formData[setiIdentifier] ||
      {}) as SourceAnalysisOrganizerFormData;

    const restoredData: Partial<SourceAnalysisOrganizerFormData> = {
      ...currentFormData,
      author: sourceAnalysisSetiData.author,
      year: sourceAnalysisSetiData.year,
    };

    optionalFieldList.forEach((field) => {
      restoredData[field] = "";
    });

    setFormData((prev) => ({
      ...prev,
      [setiIdentifier]: restoredData,
    }));

    setIsAutoFilled(false);
  };

  if (isAutoFilled) {
    return (
      <ButtonWithTrackEvent
        flavor={Button.flavors.muted}
        size={Button.sizes.lg}
        onClick={handleRestoreToDefault}
        icon={{
          SvgComponent: UndoSVG,
          alignment: Button.iconAlignments.right,
        }}
        dataProps={{
          "data-qa-selector": "graphic-organizer-restore-to-default-button",
        }}
        objectType={objectTypes.graphicOrganizer}
        actionName={actionNames.reject}
        streamProperties={{
          content_id: content.id,
          content_type: content.content_type,
        }}
        ariaProps={{
          "aria-label": "Restore graphic organizer template to default",
        }}
      >
        Restore to default
      </ButtonWithTrackEvent>
    );
  }

  return (
    <AIButtonWithTrackEvent
      alignment={Button.iconAlignments.left}
      size={Button.sizes.lg}
      onClick={handleAutoFill}
      dataProps={{
        "data-qa-selector": "graphic-organizer-luna-auto-fill-button",
      }}
      objectType={objectTypes.graphicOrganizer}
      actionName={actionNames.select}
      streamProperties={{
        content_id: content.id,
        content_type: content.content_type,
      }}
      ariaProps={{
        "aria-label": "Use Luna auto-fill to fill graphic organizer template",
      }}
    >
      Luna auto-fill
    </AIButtonWithTrackEvent>
  );
};

export default LunaAutoFillButton;
